import type CountryLocalization from '~~/types/localization/countryLocalization';
import { localizationDefinitions } from '~~/i18n';
import { getPrefixedSlug } from '~~/helpers/routes/prefixes';
import { isLocal } from '~~/helpers/env/checkEnvironmentType';
import { getDomainByCountry } from '~~/helpers/domains';
import type { CountryCodeValue } from '~~/types/localization/localizationHelperTypes';
import { addHostProtocolToDomain } from '~~/helpers/host/addHostProtocolToDomain';;

export const useLocalization = () => {

  const i18n = useI18n();

  const countryLocalization = computed<CountryLocalization>(
    () => localizationDefinitions.getByCountryCode(
      i18n.localeProperties.value.country as CountryCodeValue) as CountryLocalization);

  /**
   * @returns locale in 5 characters standard, like `en-GB`
   * The property `code` is a mandatory i18n field and contains the language and country code.
  */
  const languageAndCountry: ComputedRef<string> = computed(() => i18n.localeProperties.value.code);

  /**
   * @returns language code in 2 characters standard, like `en`
  */
  const language = computed(
    () => countryLocalization.value.getLanguageFromExistingLanguageAndCountry(languageAndCountry.value));

  const countryCode = computed(() => countryLocalization.value.countryCode);
  const currencyCode = computed(() => countryLocalization.value.currencyCode);
  const isDefaultLanguageAndCountry = computed(
    () => countryLocalization.value.isDefaultLanguageAndCountry(languageAndCountry.value));
  const countryLabel = computed(() => countryLocalization.value.label);
  const domain = computed(() => addHostProtocolToDomain(getDomainByCountry(countryCode.value)));
  const buildLanguagePrefixedUrlBasedOnActiveLanguage = (urlSlug: string) => {

    if (isDefaultLanguageAndCountry.value) {

      return urlSlug;

    }

    return getPrefixedSlug(urlSlug, language.value);

  };

  const clientSideDomain = computed(() => {

    if (import.meta.client) {

      const { hostname, port } = window.location;
      const hostWithProtocol = addHostProtocolToDomain(hostname);

      if (isLocal()) {

        return `${hostWithProtocol}${port ? `:${port}` : ''}`;

      } else {

        return hostWithProtocol;

      }

    } else {

      throw new Error('clientSideDomain can only be used on the client side');

    }

  });

  return {
    currencyCode,
    languageAndCountry,
    countryCode,
    countryLocalization,
    countryLabel,
    isDefaultLanguageAndCountry,
    language,
    buildLanguagePrefixedUrlBasedOnActiveLanguage,
    domain,
    clientSideDomain,
  };

};
